import React, { useState } from 'react';
import { Menu, X, ChevronRight, Award, Users, Target, CheckCircle, ArrowLeft, FileText, Search, Shield, Star, Calendar, Phone, Clock, TrendingUp, BarChart3, Zap, Settings } from 'lucide-react';

const InnoMediqWebsite = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Service-Daten für alle Leistungen
  const servicesData = {
    'gap-analyse': {
      title: 'GAP-Analyse',
      subtitle: 'Systematische Analyse Ihres Qualitätsmanagementsystems',
      description: 'Unsere GAP-Analyse deckt Schwachstellen in Ihrem QMS auf und zeigt konkrete Handlungsfelder für die Optimierung. Wir bewerten systematisch alle Bereiche nach ISO 13485 und erstellen einen detaillierten Maßnahmenkatalog.',
      benefits: [
        'Transparente Bewertung aller QMS-Bereiche',
        'Identifikation kritischer Compliance-Lücken',
        'Priorisierte Handlungsempfehlungen',
        'Kosten-Nutzen-Bewertung der Maßnahmen'
      ],
      process: [
        'Dokumentenanalyse und Vorbereitung',
        'Vor-Ort-Bewertung mit Ihrem Team',
        'Detaillierte Auswertung und Analyse',
        'Präsentation der Ergebnisse und Empfehlungen'
      ],
      duration: '2-4 Wochen',
      deliverables: [
        'Umfassender GAP-Analyse-Report',
        'Priorisierter Maßnahmenkatalog',
        'Zeitplan für die Umsetzung',
        'Kosten-Nutzen-Bewertung'
      ]
    },
    'audit': {
      title: 'Audit Services',
      subtitle: 'Professionelle Audits für Ihr Qualitätsmanagementsystem',
      description: 'Unsere erfahrenen Auditoren führen interne Audits, Lieferantenaudits und Zertifizierungsvorbereitungen durch. Wir helfen Ihnen dabei, Ihr QMS kontinuierlich zu verbessern und auf Zertifizierungen vorzubereiten.',
      benefits: [
        'Objektive Bewertung durch externe Experten',
        'Vorbereitung auf Zertifizierungsaudits',
        'Identifikation von Verbesserungspotenzialen',
        'Stärkung der Audit-Kompetenz im Team'
      ],
      process: [
        'Audit-Planung und Terminabstimmung',
        'Durchführung der Audit-Aktivitäten',
        'Dokumentation und Bewertung',
        'Audit-Bericht und Nachbesprechung'
      ],
      duration: '1-3 Wochen',
      deliverables: [
        'Detaillierter Audit-Bericht',
        'Nichtkonformitäten-Liste',
        'Empfehlungen zur Verbesserung',
        'Follow-up-Plan'
      ]
    },
    'qms': {
      title: 'QMS Aufbau',
      subtitle: 'Implementierung und Optimierung von Qualitätsmanagementsystemen',
      description: 'Wir unterstützen Sie beim Aufbau eines robusten QMS nach ISO 13485. Von der Prozessanalyse bis zur erfolgreichen Zertifizierung begleiten wir Sie strukturiert durch alle Phasen.',
      benefits: [
        'Strukturierte Implementierung nach ISO 13485',
        'Anpassung an Ihre Unternehmensprozesse',
        'Effiziente Dokumentenstruktur',
        'Vorbereitung auf die Zertifizierung'
      ],
      process: [
        'IST-Analyse und Konzeptentwicklung',
        'Prozessdesign und Dokumentation',
        'Implementierung und Schulung',
        'Validierung und Zertifizierungsvorbereitung'
      ],
      duration: '3-6 Monate',
      deliverables: [
        'QMS-Handbuch',
        'Prozessdokumentation',
        'Arbeitsanweisungen',
        'Schulungsunterlagen'
      ]
    },
    'capa': {
      title: 'CAPA Management',
      subtitle: 'Corrective and Preventive Action Management',
      description: 'Unser CAPA-Management-System hilft Ihnen dabei, Probleme systematisch zu analysieren, Korrekturmaßnahmen zu implementieren und präventive Maßnahmen zu etablieren.',
      benefits: [
        'Strukturierte Problemanalyse',
        'Effektive Korrekturmaßnahmen',
        'Präventive Risikoreduzierung',
        'Kontinuierliche Verbesserung'
      ],
      process: [
        'Problem-Identifikation und -bewertung',
        'Ursachenanalyse (Root Cause Analysis)',
        'Maßnahmenplanung und -umsetzung',
        'Wirksamkeitskontrolle und Follow-up'
      ],
      duration: '2-4 Wochen',
      deliverables: [
        'CAPA-Verfahren und Prozesse',
        'Analyse-Tools und Templates',
        'Tracking-System',
        'Schulungsunterlagen'
      ]
    },
    'change': {
      title: 'Changemanagement',
      subtitle: 'Strukturierte Begleitung von Veränderungsprozessen',
      description: 'Veränderungen in Qualitätsmanagementsystemen erfordern strukturiertes Vorgehen. Wir begleiten Sie bei der Anpassung von Prozessen, Schulung der Mitarbeiter und erfolgreichen Implementierung.',
      benefits: [
        'Strukturierte Veränderungsprozesse',
        'Minimierung von Implementierungsrisiken',
        'Mitarbeiterschulung und -akzeptanz',
        'Kontinuierliche Begleitung'
      ],
      process: [
        'Change-Analyse und Planung',
        'Stakeholder-Management',
        'Implementierung und Schulung',
        'Monitoring und Anpassung'
      ],
      duration: '4-8 Wochen',
      deliverables: [
        'Change-Management-Plan',
        'Kommunikationsstrategie',
        'Schulungskonzept',
        'Erfolgsmessung'
      ]
    }
  };

  const handleNavigation = (page) => {
    setCurrentPage(page);
    setIsMenuOpen(false);
  };

  const handleServiceClick = (serviceKey) => {
    setCurrentPage(serviceKey);
    setIsMenuOpen(false);
  };

  // Header Component
  const Header = () => (
    <header style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      backgroundColor: 'white',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      zIndex: 1000,
      padding: '1rem 20px'
    }}>
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <div 
          onClick={() => handleNavigation('home')}
          style={{ 
            fontSize: '1.5rem', 
            fontWeight: 'bold', 
            color: '#1e3a8a',
            cursor: 'pointer'
          }}
        >
          InnoMediq
        </div>
        
        <nav style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
          <button 
            onClick={() => handleNavigation('home')}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: currentPage === 'home' ? '#1e3a8a' : '#6b7280',
              cursor: 'pointer',
              fontWeight: currentPage === 'home' ? '600' : '400'
            }}
          >
            Home
          </button>
          <button 
            onClick={() => handleNavigation('services')}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: currentPage === 'services' ? '#1e3a8a' : '#6b7280',
              cursor: 'pointer',
              fontWeight: currentPage === 'services' ? '600' : '400'
            }}
          >
            Leistungen
          </button>
          <button 
            onClick={() => handleNavigation('about')}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: currentPage === 'about' ? '#1e3a8a' : '#6b7280',
              cursor: 'pointer',
              fontWeight: currentPage === 'about' ? '600' : '400'
            }}
          >
            Über uns
          </button>
          <button 
            onClick={() => handleNavigation('contact')}
            style={{
              backgroundColor: '#1e3a8a',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Kontakt
          </button>
        </nav>
      </div>
    </header>
  );

  // Service Detail Page Component
  const ServiceDetailPage = ({ serviceKey }) => {
    const service = servicesData[serviceKey];
    
    return (
      <div style={{ paddingTop: '80px', minHeight: '100vh' }}>
        {/* Back Button */}
        <div style={{ padding: '20px', backgroundColor: '#f9fafb' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <button 
              onClick={() => handleNavigation('home')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                color: '#1e3a8a',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              <ArrowLeft size={20} />
              Zurück zur Startseite
            </button>
          </div>
        </div>

        {/* Service Header */}
        <section style={{ padding: '3rem 20px', backgroundColor: '#1e3a8a', color: 'white' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
            <h1 style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              {service.title}
            </h1>
            <p style={{ fontSize: '1.25rem', color: '#bfdbfe', marginBottom: '2rem' }}>
              {service.subtitle}
            </p>
            <button 
              onClick={() => handleNavigation('contact')}
              style={{
                backgroundColor: '#eab308',
                color: '#1e3a8a',
                padding: '12px 24px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Jetzt Beratung anfragen
            </button>
          </div>
        </section>

        {/* Service Content */}
        <section style={{ padding: '4rem 20px', backgroundColor: 'white' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '3rem', marginBottom: '3rem' }}>
              {/* Main Content */}
              <div>
                <h2 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1e3a8a' }}>
                  Überblick
                </h2>
                <p style={{ fontSize: '1.125rem', color: '#6b7280', lineHeight: '1.7', marginBottom: '2rem' }}>
                  {service.description}
                </p>

                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1e3a8a' }}>
                  Ihre Vorteile
                </h3>
                <div style={{ marginBottom: '2rem' }}>
                  {service.benefits.map((benefit, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.75rem', 
                      marginBottom: '0.75rem' 
                    }}>
                      <CheckCircle size={20} color="#10b981" />
                      <span style={{ color: '#374151', fontSize: '1rem' }}>{benefit}</span>
                    </div>
                  ))}
                </div>

                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1e3a8a' }}>
                  Unser Vorgehen
                </h3>
                <div style={{ marginBottom: '2rem' }}>
                  {service.process.map((step, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '1rem', 
                      marginBottom: '1rem',
                      padding: '1rem',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px'
                    }}>
                      <div style={{
                        width: '30px',
                        height: '30px',
                        backgroundColor: '#1e3a8a',
                        color: 'white',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        flexShrink: 0
                      }}>
                        {index + 1}
                      </div>
                      <span style={{ color: '#374151', fontSize: '1rem' }}>{step}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sidebar */}
              <div style={{ backgroundColor: '#f9fafb', padding: '2rem', borderRadius: '12px', height: 'fit-content' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1e3a8a' }}>
                  Projekteckdaten
                </h3>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Clock size={16} color="#6b7280" />
                    <span style={{ fontWeight: '600', color: '#374151' }}>Projektdauer:</span>
                  </div>
                  <p style={{ color: '#6b7280', marginLeft: '1.5rem' }}>{service.duration}</p>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <FileText size={16} color="#6b7280" />
                    <span style={{ fontWeight: '600', color: '#374151' }}>Lieferumfang:</span>
                  </div>
                  <div style={{ marginLeft: '1.5rem' }}>
                    {service.deliverables.map((item, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem', 
                        marginBottom: '0.5rem' 
                      }}>
                        <div style={{ 
                          width: '4px', 
                          height: '4px', 
                          backgroundColor: '#6b7280', 
                          borderRadius: '50%' 
                        }} />
                        <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button 
                  onClick={() => handleNavigation('contact')}
                  style={{
                    width: '100%',
                    backgroundColor: '#1e3a8a',
                    color: 'white',
                    padding: '12px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Kostenlose Beratung
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  };

  // Homepage Component
  const Homepage = () => {
    const services = [
      {
        key: 'gap-analyse',
        title: 'GAP-Analyse',
        icon: '🔍',
        description: 'Identifizierung von Schwachstellen in Ihrem QMS und systematische Analyse bestehender Prozesse.',
        features: ['Transparente QMS-Bewertung', 'Compliance-Check', 'Handlungsempfehlungen']
      },
      {
        key: 'audit',
        title: 'Audit Services',
        icon: '📋',
        description: 'Professionelle interne, Lieferanten- und Kundenaudits für Ihr Qualitätsmanagementsystem.',
        features: ['Interne Audits', 'Lieferantenaudits', 'Zertifizierungsvorbereitung']
      },
      {
        key: 'qms',
        title: 'QMS Aufbau',
        icon: '⚙️',
        description: 'Implementierung und Optimierung von Qualitätsmanagementsystemen nach ISO 13485.',
        features: ['Strukturierte Implementierung', 'Compliance-Sicherung', 'Prozessoptimierung']
      },
      {
        key: 'capa',
        title: 'CAPA Management',
        icon: '🔧',
        description: 'Corrective and Preventive Action Management für nachhaltige Qualitätsverbesserung.',
        features: ['Problemanalyse', 'Korrekturmaßnahmen', 'Präventive Maßnahmen']
      },
      {
        key: 'change',
        title: 'Changemanagement',
        icon: '🔄',
        description: 'Strukturierte Begleitung von Veränderungsprozessen in Ihrem Qualitätsmanagementsystem.',
        features: ['Prozessanpassung', 'Mitarbeiterschulung', 'Implementierungsbegleitung']
      },
      {
        key: 'regulatory',
        title: 'Regulatory',
        icon: '🔄',
        description: 'Strukturierte Begleitung von Veränderungsprozessen in Ihrem Qualitätsmanagementsystem.',
        features: ['Prozessanpassung', 'Mitarbeiterschulung', 'Implementierungsbegleitung']
      }
    ];

    const testimonials = [
      {
        text: "InnoMediq hat uns dabei geholfen, unser QMS nach ISO 13485 erfolgreich zu implementieren. Die Beratung war praxisnah und zielführend.",
        author: "Dr. Sarah Weber",
        company: "MedTech Solutions GmbH",
        role: "Qualitätsmanagerin"
      },
      {
        text: "Die GAP-Analyse hat uns kritische Schwachstellen aufgezeigt. Dank der strukturierten Empfehlungen konnten wir unser System deutlich verbessern.",
        author: "Michael Hoffmann",
        company: "BioDevice AG",
        role: "Geschäftsführer"
      },
      {
        text: "Professionelle Audit-Services, die uns optimal auf die Zertifizierung vorbereitet haben. Sehr empfehlenswert!",
        author: "Lisa Müller",
        company: "CardioTech GmbH",
        role: "QM-Leiterin"
      }
    ];

    return (
      <div style={{ paddingTop: '64px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
          color: 'white',
          padding: '4rem 20px',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h1 style={{ 
              fontSize: '3rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              lineHeight: '1.2'
            }}>
              Ihr Partner für Qualitätsmanagement in der Medizintechnik
            </h1>
            <p style={{ 
              fontSize: '1.25rem', 
              color: '#bfdbfe', 
              marginBottom: '2rem',
              maxWidth: '800px',
              margin: '0 auto 2rem'
            }}>
              Wir unterstützen Medizintechnikunternehmen dabei, ihre Qualitätsmanagementsysteme 
              nach ISO 13485 zu implementieren, zu optimieren und erfolgreich zu zertifizieren.
            </p>
            <button 
              onClick={() => handleNavigation('contact')}
              style={{
                backgroundColor: '#eab308',
                color: '#1e3a8a',
                padding: '16px 32px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1.125rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Kostenlose Beratung anfragen
              <ChevronRight size={20} />
            </button>
          </div>
        </section>

        {/* Services Section */}
        <section style={{ padding: '4rem 20px', backgroundColor: '#f9fafb' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1e3a8a' }}>
                Unsere Leistungen
              </h2>
              <p style={{ fontSize: '1.125rem', color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>
                Maßgeschneiderte Qualitätsmanagement-Lösungen für Medizintechnikunternehmen
              </p>
            </div>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
              gap: '2rem' 
            }}>
              {services.map((service, index) => (
                <div 
                  key={service.key}
                  onClick={() => handleServiceClick(service.key)}
                  style={{
                    backgroundColor: 'white',
                    padding: '2rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    border: '1px solid #e5e7eb'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                    <span style={{ fontSize: '2.5rem' }}>{service.icon}</span>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1e3a8a', margin: 0 }}>
                      {service.title}
                    </h3>
                  </div>
                  
                  <p style={{ color: '#6b7280', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                    {service.description}
                  </p>
                  
                  <div style={{ marginBottom: '1.5rem' }}>
                    {service.features.map((feature, idx) => (
                      <div key={idx} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem', 
                        marginBottom: '0.5rem' 
                      }}>
                        <CheckCircle size={16} color="#10b981" />
                        <span style={{ color: '#374151', fontSize: '0.9rem' }}>{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    color: '#1e3a8a',
                    fontWeight: '600'
                  }}>
                    Mehr erfahren
                    <ChevronRight size={16} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section style={{ padding: '4rem 20px', backgroundColor: 'white' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1e3a8a' }}>
                Warum InnoMediq?
              </h2>
              <p style={{ fontSize: '1.125rem', color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>
                Unsere Expertise und Erfahrung in der Medizintechnik-Branche
              </p>
            </div>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: '2rem' 
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  backgroundColor: '#1e3a8a', 
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <Award size={40} color="white" />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Zertifizierte Experten
                </h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
                  Unsere Berater sind zertifizierte Auditoren und Experten für ISO 13485 
                  mit jahrelanger Branchenerfahrung.
                </p>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  backgroundColor: '#1e3a8a', 
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <Target size={40} color="white" />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Praxisnahe Lösungen
                </h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
                  Maßgeschneiderte Konzepte, die auf Ihre spezifischen Anforderungen 
                  und Unternehmensprozesse abgestimmt sind.
                </p>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  backgroundColor: '#1e3a8a', 
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <Users size={40} color="white" />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Persönliche Betreuung
                </h3>
                <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
                  Direkter Kontakt zu Ihrem Projektleiter und kontinuierliche 
                  Begleitung während des gesamten Implementierungsprozesses.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section style={{ padding: '4rem 20px', backgroundColor: '#f9fafb' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1e3a8a' }}>
                Das sagen unsere Kunden
              </h2>
              <p style={{ fontSize: '1.125rem', color: '#6b7280' }}>
                Erfolgsgeschichten aus der Praxis
              </p>
            </div>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '2rem' 
            }}>
              {testimonials.map((testimonial, index) => (
                <div key={index} style={{
                  backgroundColor: 'white',
                  padding: '2rem',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem' }}>
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} color="#eab308" fill="#eab308" />
                    ))}
                  </div>
                  <p style={{ 
                    color: '#374151', 
                    fontStyle: 'italic', 
                    marginBottom: '1.5rem', 
                    lineHeight: '1.6' 
                  }}>
                    "{testimonial.text}"
                  </p>
                  <div>
                    <p style={{ fontWeight: 'bold', color: '#1e3a8a', marginBottom: '0.25rem' }}>
                      {testimonial.author}
                    </p>
                    <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                      {testimonial.role}, {testimonial.company}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{
          backgroundColor: '#1e3a8a',
          color: 'white',
          padding: '4rem 20px',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Bereit für den nächsten Schritt?
            </h2>
            <p style={{ fontSize: '1.125rem', color: '#bfdbfe', marginBottom: '2rem' }}>
              Lassen Sie uns gemeinsam Ihr Qualitätsmanagementsystem optimieren. 
              Kontaktieren Sie uns für eine kostenlose Erstberatung.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button 
                onClick={() => handleNavigation('contact')}
                style={{
                  backgroundColor: '#eab308',
                  color: '#1e3a8a',
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Phone size={20} />
                Jetzt Termin vereinbaren
              </button>
              <button 
                onClick={() => handleNavigation('blog')}
                style={{
                  backgroundColor: 'transparent',
                  color: 'white',
                  padding: '12px 24px',
                  border: '2px solid white',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FileText size={20} />
                Ratgeber lesen
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  };

  // Contact Page Component
  const ContactPage = () => (
    <div style={{ paddingTop: '80px', minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 20px' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{ fontSize: '3rem', fontWeight: 'bold', color: '#1e3a8a', marginBottom: '1rem' }}>
            Kontakt
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#6b7280' }}>
            Lassen Sie uns über Ihr Qualitätsmanagement sprechen
          </p>
        </div>
        
        <div style={{ 
          backgroundColor: 'white', 
          padding: '3rem', 
          borderRadius: '12px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e3a8a', marginBottom: '2rem' }}>
            Kostenlose Erstberatung
          </h2>
          <p style={{ fontSize: '1.125rem', color: '#6b7280', marginBottom: '2rem' }}>
            Vereinbaren Sie jetzt ein unverbindliches Beratungsgespräch mit unseren Experten.
          </p>
          <div style={{ display: 'flex', gap: '2rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'left' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1e3a8a', marginBottom: '1rem' }}>
                Kontaktdaten
              </h3>
              <p style={{ marginBottom: '0.5rem' }}>
                <strong>Email:</strong> info@innomediq.de
              </p>
              <p style={{ marginBottom: '0.5rem' }}>
                <strong>Telefon:</strong> +49 (0) 123 456 789
              </p>
              <p>
                <strong>Online-Termin:</strong> calendly.com/innomediq
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Simple placeholder pages
  const ServicesPage = () => (
    <div style={{ paddingTop: '80px', minHeight: '100vh', padding: '2rem 20px', textAlign: 'center' }}>
      <h1 style={{ fontSize: '3rem', fontWeight: 'bold', color: '#1e3a8a', marginBottom: '2rem' }}>
        Alle Leistungen
      </h1>
      <p style={{ fontSize: '1.125rem', color: '#6b7280', marginBottom: '2rem' }}>
        Hier finden Sie eine Übersicht aller unserer Leistungen
      </p>
      <button 
        onClick={() => handleNavigation('home')}
        style={{
          backgroundColor: '#1e3a8a',
          color: 'white',
          padding: '12px 24px',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer'
        }}
      >
        Zurück zur Startseite
      </button>
    </div>
  );

  const AboutPage = () => (
    <div style={{ paddingTop: '80px', minHeight: '100vh', padding: '2rem 20px', textAlign: 'center' }}>
      <h1 style={{ fontSize: '3rem', fontWeight: 'bold', color: '#1e3a8a', marginBottom: '2rem' }}>
        Über uns
      </h1>
      <p style={{ fontSize: '1.125rem', color: '#6b7280', marginBottom: '2rem' }}>
        Lernen Sie unser Team und unsere Mission kennen
      </p>
      <button 
        onClick={() => handleNavigation('home')}
        style={{
          backgroundColor: '#1e3a8a',
          color: 'white',
          padding: '12px 24px',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer'
        }}
      >
        Zurück zur Startseite
      </button>
    </div>
  );

  // Main render logic
  const renderPage = () => {
    if (currentPage === 'home') return <Homepage />;
    if (currentPage === 'contact') return <ContactPage />;
    if (currentPage === 'services') return <ServicesPage />;
    if (currentPage === 'about') return <AboutPage />;
    
    // Check if it's a service detail page
    if (servicesData[currentPage]) {
      return <ServiceDetailPage serviceKey={currentPage} />;
    }
    
    // Default fallback
    return <Homepage />;
  };

  // Footer Component
  const Footer = () => (
    <footer style={{ backgroundColor: '#1e3a8a', color: 'white', padding: '3rem 20px 2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '2rem',
          marginBottom: '2rem'
        }}>
          {/* Company Info */}
          <div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              InnoMediq
            </h3>
            <p style={{ color: '#bfdbfe', lineHeight: '1.6', marginBottom: '1rem' }}>
              Ihr vertrauensvoller Partner für Qualitätsmanagement in der Medizintechnik. 
              Wir helfen Ihnen dabei, ISO 13485 erfolgreich zu implementieren.
            </p>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                backgroundColor: '#3b82f6', 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}>
                📧
              </div>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                backgroundColor: '#3b82f6', 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}>
                📱
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Leistungen
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <button 
                  onClick={() => handleServiceClick('gap-analyse')}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#bfdbfe', 
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  GAP-Analyse
                </button>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <button 
                  onClick={() => handleServiceClick('audit')}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#bfdbfe', 
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Audit Services
                </button>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <button 
                  onClick={() => handleServiceClick('qms')}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#bfdbfe', 
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  QMS Aufbau
                </button>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <button 
                  onClick={() => handleServiceClick('capa')}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#bfdbfe', 
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  CAPA Management
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleServiceClick('change')}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#bfdbfe', 
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Changemanagement
                </button>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Schnellzugang
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <button 
                  onClick={() => handleNavigation('home')}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#bfdbfe', 
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Startseite
                </button>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <button 
                  onClick={() => handleNavigation('about')}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#bfdbfe', 
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Über uns
                </button>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <button 
                  onClick={() => handleNavigation('contact')}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#bfdbfe', 
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Kontakt
                </button>
              </li>
              <li>
                <span style={{ color: '#bfdbfe', cursor: 'pointer', textDecoration: 'underline' }}>
                  Impressum
                </span>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              Kontakt
            </h4>
            <div style={{ color: '#bfdbfe', lineHeight: '1.6' }}>
              <p style={{ marginBottom: '0.5rem' }}>
                📧 info@innomediq.de
              </p>
              <p style={{ marginBottom: '0.5rem' }}>
                📞 +49 (0) 123 456 789
              </p>
              <p style={{ marginBottom: '0.5rem' }}>
                📅 calendly.com/innomediq
              </p>
              <p>
                📍 München, Deutschland
              </p>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div style={{ 
          borderTop: '1px solid #3b82f6', 
          paddingTop: '2rem', 
          textAlign: 'center', 
          color: '#bfdbfe' 
        }}>
          <p>© 2025 InnoMediq. Alle Rechte vorbehalten.</p>
        </div>
      </div>
    </footer>
  );

  return (
    <div style={{ fontFamily: 'Arial, sans-serif' }}>
      <Header />
      {renderPage()}
      <Footer />
    </div>
  );
};

export default InnoMediqWebsite;